import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Download, Megaphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/api";
import { SEMINAR } from "@/seminars";

export function WaitlistTable() {
  const [announcing, setAnnouncing] = useState(false);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-notify"],
    queryFn: async () => (await adminApi.get("/admin/notify")).data,
    refetchInterval: 60_000,
  });

  const items = data?.items || [];

  const exportCsv = async () => {
    try {
      const { data: blob } = await adminApi.get("/admin/notify/export", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${SEMINAR.id}-waitlist-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const announce = async () => {
    setAnnouncing(true);
    try {
      const { data: res } = await adminApi.post("/admin/notify/announce");
      if (res.status === "skipped") {
        toast.error("Email is not configured — nothing was sent.");
      } else {
        toast.success(`Announcement sent to ${res.sent} · ${res.skipped} skipped · ${res.failed} failed`);
      }
      refetch();
    } catch {
      toast.error("Could not send the announcement");
    } finally {
      setAnnouncing(false);
    }
  };

  const pending = items.filter((e) => !e.announced_at).length;

  return (
    <div className="admin-card p-6" data-testid="waitlist-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="admin-kicker">New-date waitlist</p>
          <p className="mt-1 text-xs text-white/45">Visitors who asked to be notified when new sessions open · {data?.total ?? 0} total · {pending} not yet emailed</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={items.length === 0} className="h-10 rounded-xl border-white/15 bg-white/5 text-xs hover:bg-white/10" data-testid="waitlist-export-btn">
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={items.length === 0 || announcing} className="h-10 rounded-xl bg-[#f2d28f] text-xs font-bold text-[#07130f] hover:bg-[#f2d28f]/90" data-testid="waitlist-announce-btn">
                <Megaphone className="mr-1.5 h-3.5 w-3.5" /> {announcing ? "Sending…" : "Announce new dates"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="waitlist-announce-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Email the waitlist the new dates?</AlertDialogTitle>
                <AlertDialogDescription>
                  This sends a confirmed-dates announcement with a sign-up link to everyone on the waitlist who hasn't already received these dates ({pending} {pending === 1 ? "person" : "people"}). Each person is emailed in their chosen language.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="waitlist-announce-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={announce} data-testid="waitlist-announce-confirm">Send announcement</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {isLoading && <p className="text-sm text-white/50">Loading waitlist…</p>}
      {!isLoading && items.length === 0 && <p className="text-sm text-white/50" data-testid="waitlist-empty">No one has joined the waitlist yet.</p>}

      {items.length > 0 && (
        <ul className="divide-y divide-white/8" data-testid="waitlist-list">
          {items.map((entry) => (
            <li key={entry.id} className="flex items-center justify-between gap-3 py-3 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate font-semibold" data-testid={`waitlist-email-${entry.id}`}>{entry.email}</span>
                {entry.announced_at && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" data-testid={`waitlist-announced-${entry.id}`} title="Announcement sent" />}
              </span>
              <span className="flex shrink-0 items-center gap-3 text-[11px] text-white/45">
                <span className="uppercase tracking-wide text-[#f2d28f]">{entry.page_language}</span>
                <span>{dayjs(entry.created_at).format("DD MMM, HH:mm")}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
